import { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from '../hooks/useForm';
import { useVerifyEmail } from '../hooks/useVerifyEmail';
import { useResendToken } from '../hooks/useResendToken';
import '../auth.css';
import Header from '../header';

export default function VerifyEmail() {
  const { t } = useTranslation();
  const [success, setSuccess] = useState<boolean>(false);
  const [showEmailInput, setShowEmailInput] = useState<boolean>(false);
  const [hasAttemptedVerification, setHasAttemptedVerification] =
    useState<boolean>(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const verificationAttempted = useRef<boolean>(false);

  const { verifyEmail, loading, error } = useVerifyEmail();
  const { resendToken, resending } = useResendToken();

  // Get token from URL parameters
  const tokenFromUrl = searchParams.get('token');

  const {
    values: tokenValues,
    handleChange: handleTokenChange,
    handleSubmit: handleVerifySubmit,
    setValues: setTokenValues,
  } = useForm({
    initialValues: { token: tokenFromUrl || '' },
    onSubmit: async data => {
      console.log('Manual form submission triggered');
      console.log('hasAttemptedVerification:', hasAttemptedVerification);
      console.log(
        'verificationAttempted.current:',
        verificationAttempted.current
      );

      // Allow retry if previous attempt failed
      verificationAttempted.current = true;
      setHasAttemptedVerification(true);

      try {
        const result = await verifyEmail(data.token);
        console.log('Manual verification result:', result);
        // If we get here, verification was successful
        setSuccess(true);
        // Redirect to home page after 3 seconds
        setTimeout(() => {
          navigate('/');
        }, 3000);
      } catch (error) {
        console.error('Manual verification error:', error);
        setHasAttemptedVerification(false); // Reset on failure
        verificationAttempted.current = false; // Reset ref on failure
      }
    },
  });

  // Auto-verify if token is present in URL
  useEffect(() => {
    if (
      tokenFromUrl &&
      !success &&
      !loading &&
      !verificationAttempted.current
    ) {
      verificationAttempted.current = true;

      // Auto-populate the token field
      setTokenValues({ token: tokenFromUrl });

      // Auto-submit the form after a short delay to show the token
      const timeoutId = setTimeout(async () => {
        console.log('Starting auto-verification...');
        setHasAttemptedVerification(true);
        try {
          const result = await verifyEmail(tokenFromUrl);
          console.log('Verification result:', result);
          // If we get here, verification was successful
          console.log('Verification successful!');
          setSuccess(true);
          setTimeout(() => {
            navigate('/');
          }, 3000);
        } catch (error) {
          console.error('Verification error:', error);
          setHasAttemptedVerification(false); // Reset on failure
          verificationAttempted.current = false; // Reset ref on failure
        }
      }, 1000);

      // Cleanup function to clear timeout if component unmounts
      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [tokenFromUrl, success, loading, verifyEmail, setTokenValues, navigate]);

  const {
    values: emailValues,
    handleChange: handleEmailChange,
    handleSubmit: handleResendSubmit,
    resetForm,
  } = useForm({
    initialValues: { email: '' },
    onSubmit: async data => {
      const result = await resendToken(data.email);
      if (result) {
        setShowEmailInput(false);
        resetForm();
      }
    },
  });

  return (
    <div className="min-vh-100 d-flex flex-column">
      <Header />
      <div className="min-vh-100 d-flex flex-column justify-content-center align-items-center">
        <div className="col-md-5 form_card">
          <h2 className="mb-4 text-center fw-bold text-primary">
            {t('verify_email.title')}
          </h2>

          {!success ? (
            <>
              {tokenFromUrl ? (
                <p className="text-center mb-4">
                  <i className="bi bi-check-circle text-success me-2"></i>
                  {t('verify_email.token_detected')}
                </p>
              ) : (
                <p className="text-center mb-4">
                  {t('verify_email.token_instruction')}
                </p>
              )}

              {error && <div className="alert alert-danger">{error}</div>}

              <form onSubmit={handleVerifySubmit}>
                <div className="mb-3">
                  <label className="form-label">{t('verify_email.token_label')}</label>
                  <input
                    type="text"
                    className="form-control custom-input"
                    placeholder={
                      tokenFromUrl
                        ? t('verify_email.token_loaded')
                        : t('verify_email.token_paste')
                    }
                    name="token"
                    value={tokenValues.token}
                    onChange={handleTokenChange}
                    required
                    readOnly={!!tokenFromUrl}
                  />
                  <small className="d-block mt-2 text-muted">
                    {t('verify_email.check_inbox')}
                  </small>
                </div>
                <button
                  type="submit"
                  className="btn btn-custom w-100"
                  disabled={loading}
                >
                  {loading
                    ? t('verify_email.verifying')
                    : hasAttemptedVerification
                      ? t('verify_email.retry')
                      : tokenFromUrl
                        ? t('verify_email.verify_auto')
                        : t('verify_email.verify')}
                </button>
              </form>

              <div className="text-center mt-3">
                <small>
                  {t('verify_email.no_email')}{' '}
                  <button
                    type="button"
                    className=" text-href border-0 bg-transparent p-0"
                    onClick={() => setShowEmailInput(!showEmailInput)}
                  >
                    {t('verify_email.resend')}
                  </button>
                </small>
              </div>

              {showEmailInput && (
                <div className="mt-3">
                  <form onSubmit={handleResendSubmit}>
                    <div className="mb-3">
                      <label className="form-label">{t('verify_email.email_label')}</label>
                      <input
                        type="email"
                        className="form-control custom-input"
                        placeholder={t('verify_email.email_placeholder')}
                        name="email"
                        value={emailValues.email}
                        onChange={handleEmailChange}
                        required
                      />
                    </div>
                    <button
                      type="submit"
                      className="btn btn-custom w-100"
                      disabled={resending}
                    >
                      {resending ? t('verify_email.resending') : t('verify_email.resend_token')}
                    </button>
                  </form>
                </div>
              )}
            </>
          ) : (
            <div className="text-center">
              <div className="mb-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="80"
                  height="80"
                  fill="#03FFA2"
                  className="bi bi-check-circle-fill"
                  viewBox="0 0 16 16"
                >
                  <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0m-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z" />
                </svg>
              </div>
              <h4 className="text-success mb-3">
                {t('verify_email.success_title')}
              </h4>
              <p>{t('verify_email.redirecting')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
